"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZonedDateTime = exports.KnownTimeZone = void 0;
const Duration_1 = require("./Duration");
const String_1 = require("./String");
const PublicConfig_1 = require("../Config/PublicConfig");
const Utils_1 = require("./Utils");
const assert_1 = __importDefault(require("assert"));
/* eslint-enable prettier/prettier */
/**
 * Time zones with special roles for Visualping.
 */
var KnownTimeZone;
(function (KnownTimeZone) {
    KnownTimeZone["UTC"] = "UTC";
    KnownTimeZone["GMT"] = "UTC";
    KnownTimeZone["Berlin"] = "Europe/Berlin";
})(KnownTimeZone = exports.KnownTimeZone || (exports.KnownTimeZone = {}));
const TIMEZONE_ALIASES = {
    "europe/kyiv": "Europe/Kiev",
    "europe/kiev": "Europe/Kyiv",
};
/**
 * Class that holds multiple (sub)representations of a datetime object in a specific time zone,
 * and adds a number of date manipulation primitives.
 */
class ZonedDateTime {
    constructor(from, timeZone) {
        this._dirty = true; // flag indicating that details attributes below should be recalculated from epochMs and timezone
        this._epochMs = typeof from === "number" ? from : from.getTime();
        // eslint-disable-next-line no-null/no-null
        if (this._epochMs === null || !isFinite(this._epochMs)) {
            throw new Error("Invalid Date object " + (from === null || from === void 0 ? void 0 : from.toString()));
        }
        this._epochMs = Math.round(this._epochMs);
        this._timeZone = timeZone;
    }
    static jsonParseReviver(_k, v) {
        // Apply simple heuristic to avoid parsing just any string that is clearly not an ISO datetime
        if (typeof v === "string" && v.length >= 20 && v.length < 30 && v[10] === "T") {
            try {
                return ZonedDateTime.fromISOString(v, KnownTimeZone.UTC, false);
            }
            catch (err) {
                return v; // not an ISO datetime string but quite close
            }
        }
        return v;
    }
    /**
     * Constructs a ZonedDateTime object as current date time.
     * If absent, timeZone is considered to be UTC.
     */
    static now(timeZone = KnownTimeZone.UTC) {
        return ZonedDateTime.fromJsDate(new Date(), timeZone);
    }
    /**
     * Constructs a ZonedDateTime from parts.
     */
    static fromParts(parts) {
        var _a, _b, _c, _d, _e;
        const z = String_1.padZeroes;
        // prettier-ignore
        const mysqlDateTime3Str = `${z(parts.year, 4)}-${z(parts.month + 1, 2)}-${z(parts.date, 2)} ${z((_a = parts.hours) !== null && _a !== void 0 ? _a : 0, 2)}:${z((_b = parts.minutes) !== null && _b !== void 0 ? _b : 0, 2)}:${z((_c = parts.seconds) !== null && _c !== void 0 ? _c : 0, 2)}.${z((_d = parts.milliseconds) !== null && _d !== void 0 ? _d : 0, 3)}`;
        return ZonedDateTime.fromMySql(mysqlDateTime3Str, (_e = parts.timeZone) !== null && _e !== void 0 ? _e : KnownTimeZone.UTC);
    }
    /**
     * Constructs a ZonedDateTime object from an ISO 8601 string. Formats allowed by javascript Date constructor are allowed.
     * If absent, timeZone is considered to be UTC.
     */
    static fromISOString(str, timeZone = KnownTimeZone.UTC, warn = true) {
        try {
            const match = str.match(ZonedDateTime._isoDateTimeFormatRegex);
            if (!match) {
                throw "tmp";
            }
            return new ZonedDateTime(new Date(str), timeZone);
        }
        catch (err) {
            const msg = `Invalid ISO string "${str}"`;
            if (warn) {
                console.warn(msg);
            }
            throw new Error(msg);
        }
    }
    /**
     * Constructs a ZonedDateTime object from a javascript Date object and a TZ database time zone name.
     * If absent, timeZone is considered to be UTC.
     */
    static fromJsDate(jsDate, timeZone = KnownTimeZone.UTC) {
        return new ZonedDateTime(jsDate, timeZone);
    }
    /**
     * Constructs a ZonedDateTime object from a Unix timestamp and a TZ database time zone name.
     * If absent, timeZone is considered to be UTC.
     */
    static fromEpochMilliseconds(epochMs, timeZone = KnownTimeZone.UTC) {
        return new ZonedDateTime(epochMs, timeZone);
    }
    /**
     * Ingests a datetime and reinterprets it in the provided time zone, returning a ZonedDateTime object.
     * If absent, timeZone is considered to be UTC.
     * The input datetime can be provided either as:
     * - a MySQL datetime string in the form "YYYY-MM-DD hh:mm:ss" or "YYYY-MM-DD hh:mm:ss.zzzzzzzzz"
     * - a javascript Date object obtained from a MySQL SELECT query interpreted as UTC
     * Internally searches all possible time zone offsets until it matches the input.
     * /!\ May fail if the input date time is invalid (date representations that are skipped when daylight savings skips 1 hour forward)
     * /!\ Cannot solve the ambiguous case of repeated dates (date representations that map to 2 different timestamps while daylight savings skips 1 hour backward)
     */
    static fromMySql(mysqlDateTime, timeZone = KnownTimeZone.UTC) {
        if (mysqlDateTime instanceof Date) {
            if (isNaN(mysqlDateTime.getTime())) {
                console.warn("ZonedDateTime::fromMySql silently interpreting an invalid JS Date as '1970-01-01 00:00:00'");
                mysqlDateTime = "1970-01-01 00:00:00";
            }
            else {
                mysqlDateTime = mysqlDateTime.toISOString().replace(/T/, " ").slice(0, -1);
            }
        }
        return this.fromUnzoned(mysqlDateTime, timeZone);
    }
    /**
     * toJSON should only return core data.
     */
    toJSON() {
        return {
            epochMs: this._epochMs,
            timeZone: this._timeZone,
            fullIsoString: this.details().fullIsoString,
        };
    }
    /**
     * Converts this ZonedDateTime into another time zone.
     * If absent, timeZone is considered to be UTC.
     */
    toTimeZone(timeZone) {
        return timeZone === this._timeZone ? this : ZonedDateTime.fromEpochMilliseconds(this._epochMs, timeZone);
    }
    /**
     * for compatibility with javascript Date (will go away)
     */
    getTime() {
        return this._epochMs;
    }
    /**
     * for compatibility with javascript Date (will go away)
     */
    setTime(epochMs) {
        this._epochMs = epochMs;
        this._dirty = true;
    }
    /**
     * Getters
     */
    epochMs() {
        return this._epochMs;
    }
    epoch() {
        return Duration_1.Duration.fromMilliseconds(this._epochMs);
    }
    timeZone() {
        return this._timeZone;
    }
    parts() {
        this.recalculateDetails();
        const d = this.details();
        return {
            year: d.year,
            month: d.month,
            date: d.date,
            hours: d.hours,
            minutes: d.minutes,
            seconds: d.seconds,
            milliseconds: d.milliseconds,
            timeZone: d.timeZone,
        };
    }
    details() {
        this.recalculateDetails();
        return (0, Utils_1.assertDefinedAndReturn)(this._details);
    }
    /**
     * Date arithmetic methods
     */
    plusYears(years) {
        return this.plusMonths(years * 12);
    }
    plusMonths(months) {
        const sign = months < 0 ? -1 : 1;
        const fullYears = Math.floor(Math.abs(months) / 12);
        const absMonths = Math.abs(months) - fullYears * 12;
        const thisParts = this.details();
        const parts = Object.assign(Object.assign({}, thisParts), { year: thisParts.year + sign * fullYears, month: thisParts.month + sign * absMonths });
        if (parts.month < 0) {
            parts.year--;
            parts.month += 12;
        }
        else if (parts.month > 11) {
            parts.year++;
            parts.month -= 12;
        }
        let zdt;
        for (let i = 0; i < 5; i++) {
            try {
                zdt = ZonedDateTime.fromParts(parts);
                break;
            }
            catch (err) {
                parts.date--;
            }
        }
        if (!zdt) {
            throw new Error(`ZonedDateTime.plusMonths(${months}) failed`);
        }
        return zdt;
    }
    plusDays(days) {
        const ts = this._epochMs + days * 24 * 60 * 60 * 1000;
        let zdt = ZonedDateTime.fromEpochMilliseconds(ts, this._timeZone);
        if (this.details().timeZoneOffsetMinutes !== zdt.details().timeZoneOffsetMinutes) {
            const offsetMinutes = this.details().timeZoneOffsetMinutes - zdt.details().timeZoneOffsetMinutes;
            zdt = ZonedDateTime.fromEpochMilliseconds(ts + offsetMinutes * 60 * 1000, this._timeZone);
        }
        return zdt;
    }
    plusHours(hours) {
        const ts = this._epochMs + hours * 60 * 60 * 1000;
        return ZonedDateTime.fromEpochMilliseconds(ts, this._timeZone);
    }
    plusMinutes(minutes) {
        const ts = this._epochMs + minutes * 60 * 1000;
        return ZonedDateTime.fromEpochMilliseconds(ts, this._timeZone);
    }
    plusSeconds(seconds) {
        const ts = this._epochMs + seconds * 1000;
        return ZonedDateTime.fromEpochMilliseconds(ts, this._timeZone);
    }
    plusMilliseconds(ms) {
        const ts = this._epochMs + ms;
        return ZonedDateTime.fromEpochMilliseconds(ts, this._timeZone);
    }
    plusDuration(d) {
        return this.plusMilliseconds(d.asMilliseconds());
    }
    minusDuration(d) {
        return this.plusMilliseconds(-d.asMilliseconds());
    }
    durationTo(other) {
        const ms = (other instanceof ZonedDateTime ? other._epochMs : other.getTime()) - this._epochMs;
        return Duration_1.Duration.fromMilliseconds(ms);
    }
    isBefore(ref) {
        return this.durationTo(ref).asMilliseconds() >= 0;
    }
    isAfter(ref) {
        return ref.isBefore(this);
    }
    epochEquals(other) {
        return this._epochMs === other._epochMs;
    }
    elapsed() {
        return this.durationTo(ZonedDateTime.now());
    }
    upcomingAnniversary(birthDate, period = "year") {
        switch (period) {
            case "day": {
                const days = Math.floor(birthDate.durationTo(this).asDays());
                for (let i = 0; i < 3; i++) {
                    const candidate = birthDate.plusDays(days + i);
                    if (candidate.isAfter(this)) {
                        return candidate;
                    }
                }
                assert_1.default.ok(!"corrupted");
                return ZonedDateTime.now();
            }
            case "month": {
                const months = Math.floor(birthDate.durationTo(this).asDays() / (365.25 / 12));
                for (let i = 0; i < 3; i++) {
                    const candidate = birthDate.plusMonths(months + i);
                    if (candidate.isAfter(this)) {
                        return candidate;
                    }
                }
                assert_1.default.ok(!"corrupted");
                return ZonedDateTime.now();
            }
            case "year": {
                const years = Math.floor(birthDate.durationTo(this).asDays() / 365.25);
                for (let i = 0; i < 3; i++) {
                    const candidate = birthDate.plusYears(years + i);
                    if (candidate.isAfter(this)) {
                        return candidate;
                    }
                }
                assert_1.default.ok(!"corrupted");
                return ZonedDateTime.now();
            }
        }
    }
    static getDateTimeFormat(timeZone, tryAlias = true) {
        try {
            let fmt = ZonedDateTime._intlDateFormatByTimeZone.get(timeZone);
            if (!fmt) {
                fmt = new Intl.DateTimeFormat("en-us", {
                    timeZone,
                    weekday: "short",
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hourCycle: "h23",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    fractionalSecondDigits: 3,
                    // timeZoneName: "short" <-- unfortunately outputs "PST" instead of a nice "-09:00" or "-08:00", so I still have to do a lot of handling to calculate the time offset
                });
                ZonedDateTime._intlDateFormatByTimeZone.set(timeZone, fmt);
            }
            return fmt;
        }
        catch (err) {
            const timeZoneAlias = tryAlias ? TIMEZONE_ALIASES[timeZone.toLowerCase()] : undefined;
            if (timeZoneAlias) {
                return ZonedDateTime.getDateTimeFormat(timeZoneAlias, false);
            }
            throw err;
        }
    }
    recalculateDetails() {
        var _a, _b;
        if (!this._dirty) {
            return;
        }
        const jsDate = new Date(this._epochMs);
        const z = String_1.padZeroes;
        const formatted = ZonedDateTime.getDateTimeFormat(this._timeZone).format(jsDate); // example string: "Fri, 02/26/2021, 00:58:15.528"
        const match = formatted.match(ZonedDateTime._intlDateFormatRegex);
        if (!match) {
            throw new Error(`Unexpected date format '${formatted}'`);
        }
        const dayOfWeekEnglish = match[1];
        const [month, day, year, hours, minutes, seconds] = match.slice(2, 8).map(Number);
        const ms = Number((_b = (_a = match[8]) === null || _a === void 0 ? void 0 : _a.slice(1)) !== null && _b !== void 0 ? _b : "0");
        (0, Utils_1.assertDefined)(month && day && year && hours && minutes && seconds && ms);
        const d = {
            year: year,
            month: month - 1,
            date: day,
            hours: hours,
            minutes: minutes,
            seconds: seconds,
            milliseconds: ms,
            timeZone: this._timeZone,
            monthEnglish: (0, Utils_1.assertDefinedAndReturn)(ZonedDateTime.monthsEnglish[month - 1]),
            dayOfWeek: ZonedDateTime.daysOfWeekEnglish.findIndex((it) => it === dayOfWeekEnglish),
            dayOfWeekEnglish: (0, Utils_1.assertDefinedAndReturn)(dayOfWeekEnglish),
            isoDateString: `${z(year, 4)}-${z(month, 2)}-${z(day, 2)}`,
            isoTimeString: `${z(hours, 2)}:${z(minutes, 2)}:${z(seconds, 2)}.${z(ms, 3)}`,
            fullIsoUtcString: new Date(this._epochMs).toISOString(),
        };
        const localIsoDateTime = `${d.isoDateString}T${d.isoTimeString}`;
        const utcInterpretedLocalDateTime = new Date(localIsoDateTime + "Z");
        const timeZoneOffsetMinutes = (utcInterpretedLocalDateTime.getTime() - jsDate.getTime()) / 60000;
        const offsetSign = timeZoneOffsetMinutes < 0 ? "-" : "+";
        // prettier-ignore
        const isoTimeZoneString = timeZoneOffsetMinutes === 0
            ? "Z"
            : `${offsetSign}${z(Math.floor(Math.abs(timeZoneOffsetMinutes) / 60), 2)}:${z(Math.abs(timeZoneOffsetMinutes) % 60, 2)}`;
        const fullIsoString = `${localIsoDateTime}${isoTimeZoneString}`;
        const mySqlDateTime3String = `${d.isoDateString} ${d.isoTimeString}`;
        const mySqlDateTime0String = mySqlDateTime3String.slice(0, -4);
        // prettier-ignore
        const fullRssString = `${d.dayOfWeekEnglish}, ${z(day, 2)} ${d.monthEnglish} ${z(year, 4)} ${z(hours, 2)}:${z(minutes, 2)}:${z(seconds, 2)} ${offsetSign}${z(Math.floor(Math.abs(timeZoneOffsetMinutes) / 60), 2)}${z(Math.abs(timeZoneOffsetMinutes) % 60, 2)}`;
        this._details = Object.assign(Object.assign({}, d), { timeZoneOffsetMinutes,
            isoTimeZoneString,
            fullIsoString,
            mySqlDateTime3String,
            mySqlDateTime0String,
            fullRssString });
        this._dirty = false;
    }
    /**
     * Supported input formats:
     * 2021-04-01 8:22:05
     * 2021-04-01 08:22:05
     * 2021-04-01 8:22:05.123
     * 2021-04-01 08:22:05.123
     * 2021-04-01 8:22:05.123456789
     * 2021-04-01 08:22:05.123456789
     */
    static fromUnzoned(mysqlDateTimeStr, timeZone, dstHoleRetry = false) {
        var _a, _b, _c, _d;
        // sanitize and parse input string
        const match = mysqlDateTimeStr.match(ZonedDateTime._mysqlDateTimeFormatRegex);
        if (!match) {
            const msg = `Invalid unzoned datetime "${mysqlDateTimeStr}" (1)`;
            console.warn(msg);
            throw new Error(msg);
        }
        let parsedUtcIsoJsDate;
        const m = match.slice(1, 7).map(Number);
        assert_1.default.ok(m.length >= 6 && m.every((n) => (0, Utils_1.isPositiveInteger)(n)));
        if (dstHoleRetry) {
            // best effort to support most cases of DST skip-forward holes
            m[3]++;
            if (m[3] === 24) {
                m[3] = 0;
                m[2]++;
            }
        }
        const z = (i, places) => { var _a; return (0, String_1.padZeroes)((_a = m[i]) !== null && _a !== void 0 ? _a : 0, places); };
        // prettier-ignore
        mysqlDateTimeStr = `${z(0, 4)}-${z(1, 2)}-${z(2, 2)} ${z(3, 2)}:${z(4, 2)}:${z(5, 2)}${(_b = (_a = match[7]) === null || _a === void 0 ? void 0 : _a.substring(0, 4)) !== null && _b !== void 0 ? _b : ".000"}`;
        // prettier-ignore
        let asUtcIsoString = `${z(0, 4)}-${z(1, 2)}-${z(2, 2)}T${z(3, 2)}:${z(4, 2)}:${z(5, 2)}${(_d = (_c = match[7]) === null || _c === void 0 ? void 0 : _c.substring(0, 4)) !== null && _d !== void 0 ? _d : ".000"}Z`;
        try {
            parsedUtcIsoJsDate = new Date(asUtcIsoString);
            if (!isFinite(parsedUtcIsoJsDate === null || parsedUtcIsoJsDate === void 0 ? void 0 : parsedUtcIsoJsDate.getTime())) {
                throw NaN;
            }
        }
        catch (err) {
            const msg = `Invalid unzoned datetime "${mysqlDateTimeStr}"/"${asUtcIsoString}" (2)`;
            console.warn(msg);
            throw new Error(msg);
        }
        const parsedAsUtcEpochMs = parsedUtcIsoJsDate.getTime();
        // special case: string ending with "24:00:00[.000]" which means midnight early next day
        if (asUtcIsoString.endsWith("24:00:00.000Z")) {
            asUtcIsoString = new Date(parsedAsUtcEpochMs).toISOString();
            mysqlDateTimeStr = asUtcIsoString.replace(/T/, " ").replace(/Z/, "");
        }
        // will now try all possible offsets, starting with the ones known for this timezone
        const tryOffset = (minutes) => {
            const candidateEpochMs = parsedAsUtcEpochMs + minutes * 60 * 1000;
            const zonedDateTime = ZonedDateTime.fromEpochMilliseconds(candidateEpochMs, timeZone);
            const len = Math.min(zonedDateTime.details().mySqlDateTime3String.length, mysqlDateTimeStr.length);
            const foundCorrectOffset = zonedDateTime.details().mySqlDateTime3String.slice(0, len) === mysqlDateTimeStr.slice(0, len);
            return foundCorrectOffset ? zonedDateTime : undefined;
        };
        for (const candidateOffsetMinutes of ZonedDateTime._knownTimeZoneOffsetMinutes.get(timeZone) || []) {
            const zonedDateTime = tryOffset(candidateOffsetMinutes);
            if (zonedDateTime) {
                return zonedDateTime;
            }
        }
        for (let candidateOffsetMinutes = -28 * 60; candidateOffsetMinutes < 28 * 60; candidateOffsetMinutes += 15) {
            const zonedDateTime = tryOffset(Math.round(candidateOffsetMinutes));
            if (zonedDateTime) {
                const knownOffsets = [
                    ...(ZonedDateTime._knownTimeZoneOffsetMinutes.get(timeZone) || []),
                    candidateOffsetMinutes,
                ];
                ZonedDateTime._knownTimeZoneOffsetMinutes.set(timeZone, knownOffsets);
                return zonedDateTime;
            }
        }
        // String representation most probably non-existent due to DST-induced holes. Let's retry with a 1-hour skip forward.
        if (!dstHoleRetry) {
            console.warn(`Invalid unzoned datetime "${mysqlDateTimeStr}" in timezone "${timeZone}", retrying with 1-hour skip forward (3)`);
            const ret = ZonedDateTime.fromUnzoned(mysqlDateTimeStr, timeZone, true);
            console.warn(`Successfully altered invalid unzoned datetime "${mysqlDateTimeStr}" ` +
                `in timezone "${timeZone}" with a 1-hour skip forward => ${ret.details().fullIsoString}`);
            return ret;
        }
        const msg = `Invalid unzoned datetime "${mysqlDateTimeStr}" in timezone "${timeZone}" (4)`;
        console.warn(msg);
        throw new Error(msg);
    }
    /**
     * Mosly for self-testing purposes.
     */
    findNextDaylightSavingDates(searchMonths) {
        const ret = [];
        const nearestHourEpochMs = Math.round(this._epochMs / (3600 * 1000)) * 3600 * 1000;
        for (let h = 0; h < searchMonths * 30.5 * 24; h++) {
            const epochMs = nearestHourEpochMs + Math.round(h) * 3600 * 1000;
            const zonedDateTime = ZonedDateTime.fromEpochMilliseconds(epochMs, this._timeZone);
            const zonedDateTimePrev = ZonedDateTime.fromEpochMilliseconds(epochMs - 1, this._timeZone);
            if (zonedDateTime.details().timeZoneOffsetMinutes !== zonedDateTimePrev.details().timeZoneOffsetMinutes) {
                ret.push(zonedDateTime);
                const mysqlReparsedZonedDateTime = ZonedDateTime.fromMySql(zonedDateTime.details().mySqlDateTime3String, this._timeZone);
                const mysqlReparsedZonedDateTimePrev = ZonedDateTime.fromMySql(zonedDateTimePrev.details().mySqlDateTime3String, this._timeZone);
                // prettier-ignore
                console.log(`Found that timezone ${this._timeZone} will apply DST on ${zonedDateTime.details().fullIsoUtcString}
  ${zonedDateTimePrev.details().fullIsoString} skips to ${zonedDateTime.details().fullIsoString}
    ${zonedDateTimePrev.toString("short-compact")}
  mysql-reparsed as
    ${mysqlReparsedZonedDateTimePrev.toString("short-compact")}
    ...skips to
    ${zonedDateTime.toString("short-compact")}
  mysql-reparsed as
    ${mysqlReparsedZonedDateTime.toString("short-compact")}\n`);
                if (JSON.stringify(zonedDateTime) !== JSON.stringify(mysqlReparsedZonedDateTime)) {
                    console.log("🧨🧨🧨 Reparsing from unzoned datetime showed an ambiguous timestamp in this timezone:\n" +
                        zonedDateTime.toString("short-compact") +
                        "\n" +
                        mysqlReparsedZonedDateTime.toString("short-compact"));
                }
                if (JSON.stringify(zonedDateTimePrev) !== JSON.stringify(mysqlReparsedZonedDateTimePrev)) {
                    console.log("🧨🧨🧨 Reparsing from unzoned datetime showed an ambiguous timestamp in this timezone:\n" +
                        zonedDateTimePrev.toString("short-compact") +
                        "\n" +
                        mysqlReparsedZonedDateTimePrev.toString("short-compact"));
                }
            }
        }
        return ret;
    }
    toString(level = "isoOnly") {
        if (level === "isoOnly") {
            return this.details().fullIsoString;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let obj = Object.assign({ epochMs: this._epochMs, timeZone: this._timeZone }, this.details());
        if (level.includes("short")) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            obj = Object.assign(Object.assign({}, obj), { dayOfWeek: undefined, isoDateString: undefined, isoTimeString: undefined, isoTimeZoneString: undefined, mySqlDateTime0String: undefined, fullRssString: undefined });
        }
        return JSON.stringify(obj, undefined, level.includes("compact") ? undefined : 2)
            .replace(/"/g, "")
            .replace(/,/g, ", ");
    }
    /**
     * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
     * for details about options.
     */
    toLocaleString(options) {
        var _a, _b, _c;
        try {
            const fmt = {
                timeZone: (_a = this._timeZone) !== null && _a !== void 0 ? _a : KnownTimeZone.UTC,
                dateStyle: options === null || options === void 0 ? void 0 : options.dateStyle,
                timeStyle: options === null || options === void 0 ? void 0 : options.timeStyle,
                dayPeriod: options === null || options === void 0 ? void 0 : options.dayPeriod,
                fractionalSecondDigits: options === null || options === void 0 ? void 0 : options.fractionalSecondDigits,
            };
            const intlFmt = new Intl.DateTimeFormat((_b = options === null || options === void 0 ? void 0 : options.locale) !== null && _b !== void 0 ? _b : (0, PublicConfig_1.publicConfig)().i18n.fallbackLng, fmt);
            return intlFmt.format(new Date(this._epochMs));
        }
        catch (err) {
            // assume this error is due to an unsupported/malformed locale so retry once with English
            if (((_c = options === null || options === void 0 ? void 0 : options.locale) === null || _c === void 0 ? void 0 : _c.toLowerCase()) !== "en") {
                (0, Utils_1.logNodeOnly)(`ZonedDateTime.toLocaleString() failed with locale ${JSON.stringify(options === null || options === void 0 ? void 0 : options.locale)} - retrying with EN`, { level: "warn" });
                return this.toLocaleString(Object.assign(Object.assign({}, options), { locale: "en" }));
            }
            else {
                throw err;
            }
        }
    }
}
exports.ZonedDateTime = ZonedDateTime;
ZonedDateTime.monthsEnglish = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
];
ZonedDateTime.daysOfWeekEnglish = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
ZonedDateTime._isoDateTimeFormatRegex = new RegExp("^([0-9]{4})-([0-9]{2})-([0-9]{2})T([0-9]{1,2})\\:([0-9]{2})\\:([0-9]{2})(\\.[0-9]{1,})?(Z|[+-]([0-9]{2})\\:([0-9]{2}))$");
ZonedDateTime._mysqlDateTimeFormatRegex = new RegExp("^([0-9]{4})-([0-9]{2})-([0-9]{2})\\s+([0-9]{1,2})\\:([0-9]{2})\\:([0-9]{2})(\\.([0-9]{3,}))?$");
ZonedDateTime._intlDateFormatByTimeZone = new Map();
// example of formatted date: "Fri, 04/09/2021, 18:08:09.012"
// NB: Safari ignores fractionalSecondDigits and outputs "Fri, 04/09/2021, 18:08:09"
ZonedDateTime._intlDateFormatRegex = new RegExp("^([A-Za-z]{3}),\\s*([0-9]{2})/([0-9]{2})/([0-9]{4}),\\s*([0-9]{1,2})\\:([0-9]{2})\\:([0-9]{2})(.[0-9]{3})?$");
ZonedDateTime._knownTimeZoneOffsetMinutes = new Map([
    [KnownTimeZone.UTC, [0]],
    [KnownTimeZone.GMT, [0]],
    [KnownTimeZone.Berlin, [-1 * 60, -2 * 60]],
]);
//# sourceMappingURL=ZonedDateTime.js.map